<template>
  <page
    class="base-screening-page d-flex flex-column fill-height overflow-hidden"
  >
    <template #nav-bar>
      <page-nav-bar :items="breadcrumbs" />
    </template>
    <template #header-bar>
      <page-header-bar>
        {{ pageTitle }}
        <template #actions>
          <slot name="header-actions"></slot>
        </template>
      </page-header-bar>
    </template>
    <v-container
      fluid
      class="flex-grow-1 overflow-hidden d-flex pa-0"
    >
      <v-col
        cols="3"
        class="bsp-left-panel flex-grow-1 d-flex flex-column overflow-hidden flex-nowrap pa-0"
      >
        <slot
          name="left-panel"
          v-bind="{
            selectedReference,
            selectNextReference,
            showingPending,
          }"
        >
          <v-row
            no-gutters
            class="flex-grow-0 grey lighten-3 py-6 pl-4 pr-5"
          >
            <resolution-progress
              class="flex-grow-1"
              :label="progressLabel"
              :relevant-count="loading ? 0 : relevantCount"
              :not-relevant-count="loading ? 0 : notRelevantCount"
              :not-sure-count="loading ? 0 : notSureCount"
              :total-count="loading ? 0 : totalCount"
            />
          </v-row>
          <v-row
            no-gutters
            class="d-flex flex-grow-1 flex-nowrap overflow-hidden"
          >
            <reference-log
              v-if="hasReferenceLogItems"
              v-model="selectedReferenceId"
              :items="referenceLogItems"
              class="overflow-auto grey lighten-5 fill-width"
            />
          </v-row>
          <v-row
            no-gutters
            class="d-flex flex-grow-0 bottom-page-row border-top"
          >
            <list-tabs
              v-model="selectedTab"
              :items="tabItems"
              class="fill-width"
            />
          </v-row>
        </slot>
      </v-col>
      <v-col class="bsp-center-column">
        <div
          v-if="showReferenceTitle && hasSelectedReference"
          class="bsp-content-title fill-height"
        >
          <div
            v-if="showReferenceTitle"
            class="bsp-content-title fill-height"
          >
            <div
              class="d-flex justify-space-between subtitle-1 px-12 py-4 align-center font-weight-bold grey--text text--darken-3"
            >
              {{ referenceTitle }}
            </div>
          </div>
          <v-divider />
        </div>
        <div class="bsp-content">
          <vote-status-bar
            v-if="showVoteStatusBar"
            class="bsp-content-vote-status"
            :researchers="researchersWhoVoted"
          />
          <div class="bsp-content-body">
            <page-content-placeholder
              v-if="loading"
              loading
              prompt-text="Loading"
            >
              <template #message>&nbsp;</template>
            </page-content-placeholder>
            <template v-else-if="hasSelectedReference">
              <slot
                name="middle-panel"
                v-bind="{
                  selectedReference,
                  selectNextReference,
                  showingPending,
                }"
              ></slot>
            </template>
            <template v-else>
              <page-content-placeholder
                v-if="hasReferenceLogItems"
                icon="mouse"
                prompt-text="Select a Reference"
              >
                <template #message>
                  Select a reference in the list on the left to
                  continue.
                </template>
              </page-content-placeholder>
              <page-content-placeholder
                v-else-if="!showingPending"
                icon="mouse"
                prompt-text="Select the left tab"
              >
                <template #message>
                  {{ noneResolvedMessage }}
                </template>
              </page-content-placeholder>
              <page-content-placeholder
                v-else
                :icon="
                  complete ? 'check_circle_outline' : 'assignment'
                "
                :icon-color="complete ? 'white' : undefined"
                :bubble-color="complete ? 'success' : undefined"
                :prompt-text="complete ? 'Complete' : 'No More Left'"
              >
                <template #message>
                  {{ emptyMessage }}
                </template>
                <template #action>
                  <action-button
                    color="primary"
                    @click="$emit('go-back')"
                  >
                    <v-icon left>arrow_back</v-icon>
                    <span>Return To Review</span>
                  </action-button>
                </template>
              </page-content-placeholder>
            </template>
          </div>
        </div>
        <div class="bsp-footer">
          <div
            v-if="enableVote"
            class="d-flex justify-center align-center border-top bsp-vote-panel"
          >
            <div class="subtitle-2 font-weight-bold mr-2">
              {{ voteLabel }}
            </div>
            <vote-controls
              :disabled="!hasSelectedReference"
              :hide-not-sure="disableUnsureVote"
              @vote="$emit('vote', $event)"
            />
          </div>
        </div>
      </v-col>
      <v-col
        cols="3"
        class="bsp-right-panel flex-grow-1 d-flex flex-column overflow-hidden flex-nowrap pa-0"
      >
        <slot
          name="right-panel"
          v-bind="{
            selectedReference,
            selectNextReference,
            showingPending,
          }"
        ></slot>
      </v-col>
    </v-container>
    <v-alert v-if="errorMessage" dense type="error" class="mb-0">
      {{ errorMessage }}
    </v-alert>
    <slot
      v-bind="{
        selectedReference,
        selectNextReference,
        showingPending,
      }"
    ></slot>
  </page>
</template>

<script>
import ActionButton from '@/components/Common/ActionButton';
import Page from '@/components/Common/Page';
import PageNavBar from '@/components/Common/PageNavBar';
import PageHeaderBar from '@/components/Common/PageHeaderBar';
import PageContentPlaceholder from '@/components/Common/PageContentPlaceholder';
import ResolutionProgress from '@/components/Common/ResolutionProgress';
import ReferenceLog from '@/components/References/ReferenceLog';
import ListTabs from '@/components/Common/ListTabs';
import VoteControls from '@/components/ScreenReferencesPage/VoteControls';
import VoteStatusBar from '@/components/References/VoteStatusBar';
import { Reference, ReferenceLogItemModel } from '@/models';
import { typeHelpers } from '@/modules';

export default {
  name: 'BaseScreeningPage',
  components: {
    ActionButton,
    Page,
    PageNavBar,
    PageHeaderBar,
    PageContentPlaceholder,
    ResolutionProgress,
    ReferenceLog,
    VoteControls,
    VoteStatusBar,
    ListTabs,
  },
  props: {
    pageTitle: {
      type: String,
      required: true,
    },
    progressLabel: {
      type: String,
      default: 'Progress',
    },
    emptyMessage: {
      type: String,
      default: 'No more eligible references to review.',
    },
    noneResolvedMessage: {
      type: String,
      default: 'No references have been reviewed yet.',
    },
    voteLabel: {
      type: String,
      default: 'Vote',
    },
    breadcrumbs: {
      type: Array /* of { icon: string; text: string; href: string; } */,
      default: () => [],
    },
    references: {
      type: Array,
      default: null,
      validator: (arr) =>
        !arr || typeHelpers.isArrayOfType(arr, Reference),
    },
    updatedReferences: {
      type: Array,
      default: () => [],
      validator: (arr) =>
        !arr || typeHelpers.isArrayOfType(arr, Reference),
    },
    getResearchersWhoVoted: {
      type: Function,
      default: null,
    },
    getReferenceLogItemStyle: {
      type: Function,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    relevantCount: {
      type: Number,
      default: 0,
    },
    notRelevantCount: {
      type: Number,
      default: 0,
    },
    notSureCount: {
      type: Number,
      default: 0,
    },
    remainingReferenceCount: {
      type: Number,
      default: 0,
    },
    showReferenceTitle: {
      type: Boolean,
      default: false,
    },
    enableVote: {
      type: Boolean,
      default: false,
    },
    disableUnsureVote: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      required: false,
      default: undefined,
    },
  },
  emits: ['update:selectedReference', 'go-back', 'vote'],
  data: () => ({
    selectedTab: 'PENDING',
    selectedReferenceId: null,
  }),
  computed: {
    tabItems() {
      return [
        {
          icon: 'remove_red_eye',
          value: 'PENDING',
          count: this.remainingReferenceCount,
        },
        {
          icon: 'check_circle_outline',
          value: 'COMPLETE',
          count: this.resolvedCount,
        },
      ];
    },
    referenceLogItems() {
      return this.showingReferences.map((reference) => {
        const itemStyle = this.getReferenceLogItemStyle(reference);
        return new ReferenceLogItemModel({
          reference,
          ...itemStyle,
        });
      });
    },
    showingReferences() {
      if (!this.showingPending) {
        return this.updatedReferences;
      }
      if (!this.references) {
        return [];
      }
      return this.references.filter((reference) => {
        return !this.hasUpdatedReference(reference);
      });
    },
    resolvedCount() {
      return this.updatedReferences.length;
    },
    hasReferenceLogItems() {
      return this.referenceLogItems.length > 0;
    },
    selectedReference() {
      return this.references?.find(
        (reference) => reference.id === this.selectedReferenceId
      );
    },
    hasSelectedReference() {
      return !!this.selectedReference;
    },
    showingPending() {
      return this.selectedTab === 'PENDING';
    },
    researchersWhoVoted() {
      if (!this.getResearchersWhoVoted || !this.selectedReference) {
        return [];
      }
      return this.getResearchersWhoVoted(this.selectedReference);
    },
    showVoteStatusBar() {
      return (
        this.showingPending && this.researchersWhoVoted.length > 0
      );
    },
    referenceTitle() {
      return this.selectedReference?.publication?.title;
    },
    referenceScore() {
      return (
        this.selectedReference?.autoScreeningClassification?.score ??
        null
      );
    },
    hasReferenceScore() {
      return this.referenceScore !== null;
    },
    totalCount() {
      return this.remainingReferenceCount + this.resolvedCount;
    },
    complete() {
      return (
        this.totalCount > 0 && this.remainingReferenceCount === 0
      );
    },
  },
  watch: {
    selectedReference(reference) {
      this.$emit('update:selectedReference', reference);
    },
    loading() {
      this.selectNextReference();
    },
    selectedTab() {
      this.$nextTick(() => this.selectNextReference());
    },
  },
  methods: {
    hasUpdatedReference(reference) {
      return this.updatedReferences.some(
        (updated) => updated.id === reference.id
      );
    },
    selectNextReference(referenceToSkip) {
      const skipId = referenceToSkip?.id;
      if (skipId !== this.selectedReferenceId) {
        // Avoid changing selected reference if the query was just refreshed.
        const selectedExists = this.showingReferences.some(
          (ref) => ref.id === this.selectedReferenceId
        );
        if (selectedExists) {
          return;
        }
      }

      this.selectedReferenceId =
        this.showingReferences.find((ref) => ref.id !== skipId)?.id ??
        null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';
.base-screening-page {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 100%;
}
.bsp-center-column {
  display: grid;
  grid:
    'title' auto
    'content' 1fr
    'footer' auto;
  height: 100%;
  padding: 0;
}

.bsp-content {
  display: grid;
  grid:
    'content-title' auto
    'vote-status' auto
    'body' 1fr;
  grid-area: content;
  overflow-x: hidden;
  overflow-y: auto;
}

.bsp-footer {
  grid-area: footer;
}

.bsp-content-title {
  display: grid;
  grid:
    'title' 1fr
    'divider' auto;
  min-height: $screening-page-progress-meter-height;
  background-color: white;
}

.bsp-content-vote-status {
  grid-area: vote-status;
}

.bsp-content-body {
  grid-area: body;
}

.bsp-vote-panel {
  height: $screening-page-reference-tab-height;
  background-color: map-get($grey, 'lighten-3');
}

.bsp-left-panel {
  border-right: 1px solid map-get($grey, 'lighten-2') !important;
  background-color: map-get($grey, 'lighten-5') !important;
}
.bsp-right-panel {
  border-left: 1px solid map-get($grey, 'lighten-2') !important;
  background-color: map-get($grey, 'lighten-5') !important;
}

.border-top {
  border-top: 1px solid map-get($grey, 'lighten-2') !important;
}
.border-left {
  border-left: 1px solid map-get($grey, 'lighten-2') !important;
}
.border-right {
  border-right: 1px solid map-get($grey, 'lighten-2') !important;
}
.fill-width {
  width: 100% !important;
}
.bottom-page-row {
  min-height: 80px;
}
</style>
